<template>
    <div v-if="isInitialising" class="brand-loader-wrapper">
        <brand-loader />
    </div>
    <div v-else class="document-viewer" :class="{ 'modal-mode': isModalMode }">
        <div class="breadcrumbs-holder" v-if="!props.noBreadcrumb">
            <router-link
                :to="documentListLink"
                class="mobile-link">
                <font-awesome-icon class="arrowLeft" :icon="['fas', 'arrow-left']" /> Back
            </router-link>
            <breadcrumb-with-underlining :breadcrumbItems="breadcrumbItems" class="breadcrumbs" />
        </div>
        <div class="main-content" :class="{ 'card-main': !isModalMode }">
            <report-details
                :allowZoom="false"
                :hasAccess="props.powerBiDetails.hasAccess"
                :description="props.powerBiDetails.description"
                :publication-date="props.powerBiDetails.publicationDate"
                :authors="props.powerBiDetails.authors"
                :filters="props.powerBiDetails.filters"
                :title="props.powerBiDetails.documentTitle"
                :subtitle="props.powerBiDetails.documentSubtitle"
                :access-reason="props.powerBiDetails.accessReason"
                :report-id="props.powerBiDetails.documentShortCode"
                is-power-bi
                report-type="PowerBi"
                @make-full-screen="makeFullScreen"
            />
            <div class="report-section" :class="{ 'no-access': !props.powerBiDetails.hasAccess }">
                <power-bi-embed
                    v-if="props.powerBiDetails.hasAccess && reportToken"
                    :report-token="reportToken"
                />
                <div class="no-access-window" v-if="!props.powerBiDetails.hasAccess">
                    <no-report-access />
                </div>
            </div>
            <document-comments
                v-if="isModalMode"
                class="modal-comments"
                :document-short-code="props.powerBiDetails.documentShortCode"
                :initial-comment-count="props.powerBiDetails.hasAccess ? props.powerBiDetails.commentCount : 0"
                :locked="!props.powerBiDetails.hasAccess"
                :allow-expand="props.allowCommentsExpand"
            />
        </div>
        <div v-if="!isModalMode" class="side-content">
            <div v-if="props.powerBiDetails.hasAccess" class="transpose-options">
                <primevue-button text class="plain-btn extra-padding grow" @click="showShareModal = true">
                    Share
                    <font-awesome-icon :icon="['fas', 'share-nodes']" />
                </primevue-button>
            </div>
            <div class="options">
                <document-comments
                    :document-short-code="props.powerBiDetails.documentShortCode"
                    :initial-comment-count="props.powerBiDetails.hasAccess ? props.powerBiDetails.commentCount : 0"
                    :locked="!props.powerBiDetails.hasAccess"
                    compact
                />
            </div>
            <div class="options">
                <div class="title">Contact us</div>
                <div class="description">
                    Let us know if you have any questions about this report, or research service.
                </div>
                <our-experts-section :our-experts="ourExperts" />
                <div>
                    <primevue-button
                        text
                        @click="openContactUs"
                        class="plain-btn">
                        Speak to our experts
                        <font-awesome-icon :icon="['fas', 'user']" />
                    </primevue-button>
                </div>
            </div>
        </div>
    </div>
    <contact-us v-model:visible="showContactUsModal" @close="showContactUsModal = false" :documentTitle="props.powerBiDetails.documentTitle" :our-experts="ourExperts" />
    <central-modal
        :header="props.powerBiDetails.documentTitle"
        v-model:visible="showPowerBiPopout"
        size="large"
    >
        <power-bi-embed
            class="power-bi-fullscreen"
            v-if="props.powerBiDetails.hasAccess && reportToken"
            :report-token="reportToken"
        />
    </central-modal>
    <central-modal
        v-model:visible="showShareModal"
        header="Share"
        :size="{ width: '70rem', height: '32rem' }"
    >
        <share-document-url :title="props.powerBiDetails.documentTitle" />
    </central-modal>
</template>

<script lang="ts" setup>

import { IPowerBiDetails } from '@/interfaces/deltaGpt/IDocument.ts';
import primevueButton from 'primevue/button';
import { computed, ref } from 'vue';
import { IBreadcrumbItem } from '@/interfaces/general/IBreadcrumbItem.ts';
import { createDocumentBreadcrumbItems } from '@/utils/documentBreadcrumbUtils.ts';
import { router } from '@/router.ts';
import PowerBiEmbed from '@/views/delta-gpt/PowerBiEmbed.vue';
import BreadcrumbWithUnderlining from '@/components/common/BreadcrumbWithUnderlining.vue';
import { IPowerBiReportToken } from '@/interfaces/deltaGpt/IPowerBiReportToken.ts';
import { powerBiApi } from '@/api/deltaGpt/powerBIApi.ts';
import { deltaGptApi } from '@/api/deltaGpt/deltaGptApi.ts';
import { IExpert } from '@/interfaces/deltaGpt/IExpert.ts';
import { getResearchSectionRouteName } from '@/routes/routeUtils.ts';
import { useRoute } from 'vue-router';
import { deltaGptRoutes } from '@/routes/deltaGptRoutes.ts';
import CentralModal from '@/components/common/CentralModal.vue';
import BrandLoader from '@/components/layout/BrandLoader.vue';
import ShareDocumentUrl from './ShareDocumentUrl.vue';
import ReportDetails from './ReportDetails.vue';
import DocumentComments from './DocumentComments.vue';
import NoReportAccess from './NoReportAccess.vue';
import OurExpertsSection from './OurExperts.vue';
import ContactUs from './ContactUs.vue';

const route = useRoute();

const props = withDefaults(defineProps<{
    powerBiDetails: IPowerBiDetails;
    noBreadcrumb?: boolean;
    displayMode?: 'page' | 'modal';
    allowCommentsExpand?: boolean;
}>(), {
    noBreadcrumb: false,
    displayMode: 'page',
    allowCommentsExpand: true,
});

const reportToken = ref<IPowerBiReportToken | undefined>();
const ourExperts = ref<Array<IExpert>>([]);
const showContactUsModal = ref<boolean>(false);
const showPowerBiPopout = ref<boolean>(false);
const showShareModal = ref<boolean>(false);
const isInitialising = ref<boolean>(false);
const isModalMode = computed<boolean>(() => props.displayMode === 'modal');

const documentListLink = computed(() => ({
    name: getResearchSectionRouteName(route, deltaGptRoutes.documentList.name),
    query: route.query,
}));

const breadcrumbItems = computed<Array<IBreadcrumbItem>>(() => createDocumentBreadcrumbItems(
    props.powerBiDetails.documentTitle,
    props.powerBiDetails.documentShortCode,
    (): void => {
        router.push(documentListLink.value);
    },
));

function makeFullScreen (): void {
    showPowerBiPopout.value = true;
}

function openContactUs (): void {
    showContactUsModal.value = true;
}

async function getExperts (): Promise<void> {
    // TODO: sort
    ourExperts.value = await deltaGptApi.getExperts({ subscriptions: [] });
}

async function getPowerBiToken (): Promise<void> {
    if (!props.powerBiDetails.hasAccess) return;
    reportToken.value = await powerBiApi.getPowerBiReport(props.powerBiDetails.documentShortCode);
}

async function initialise (): Promise<void> {
    isInitialising.value = true;

    try {
        const promises: Array<Promise<void>> = [];
        promises.push(getPowerBiToken());
        promises.push(getExperts());
        await Promise.all(promises);
    } finally {
        isInitialising.value = false;
    }
}

initialise();

</script>

<style lang="scss" scoped>
@use '/src/assets/scss/components/research/documentView.scss';

.brand-loader-wrapper {
    position: relative;
    height: 100%;
    min-height: 0;
}

.power-bi-fullscreen {
    height: 100%;
    overflow: hidden;
}

.document-viewer {

    &.modal-mode {
        grid-template-columns: minmax(0, 1fr);
        grid-template-areas: "main";
        overflow-y: auto;

        .main-content {
            margin: 0;
        }
    }
    .main-content {
        align-self: start;
        padding: var(--largeSpacing);
        margin: var(--extraLargeSpacing);
    }
}

.report-section {
    height: clamp(70rem, 75vh, 100rem);
    overflow: hidden !important;
    &.no-access {
        padding-top: 10rem;
        height: auto;
    }
}

.modal-comments {
    border-top: 1px solid var(--neutral300);
    margin-top: var(--largeSpacing);
    padding-top: var(--largeSpacing);
}

.side-content {
    .transpose-options {
        display: flex;
        justify-content: space-between;
        gap: var(--defaultSpacing);

        .grow {
            flex: 1;
            display: flex;
        }
    }
}

</style>
